import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Check,
    X,
    Minus,
    ArrowUpRight,
    Sparkles,
    Crown,
} from "lucide-react";
import {
    COMPARE_TOOLS,
    COMPARE_CATEGORIES,
    COMPARE_DEEP_DIVES,
} from "../data/compareContent";
import { ModuleFeaturesSection } from "../components/pricing/ModuleFeaturesSection";
import { EveryPlanIncludesSection } from "../components/pricing/EveryPlanIncludesSection";

/* ──────────────────────────────────────────────
 *  Helpers
 * ────────────────────────────────────────────── */

const CellMark = ({ v, accent }) => {
    if (v === "yes")
        return (
            <span
                className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${accent || "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"}`}
            >
                <Check className="w-3.5 h-3.5" />
            </span>
        );
    if (v === "partial")
        return (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border bg-amber-500/10 border-amber-500/30 text-amber-300">
                <Minus className="w-3.5 h-3.5" />
            </span>
        );
    if (v === "addon")
        return (
            <span className="inline-flex items-center justify-center px-2 h-6 rounded-full border bg-zinc-600/10 border-zinc-600/30 text-[10px] font-code text-zinc-400">
                add-on
            </span>
        );
    return (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border bg-rose-500/10 border-rose-500/30 text-rose-300">
            <X className="w-3.5 h-3.5" />
        </span>
    );
};

/* ──────────────────────────────────────────────
 *  Page
 * ────────────────────────────────────────────── */

export default function ComparePage() {
    const [deepDive, setDeepDive] = useState("jira");
    const active = COMPARE_DEEP_DIVES[deepDive];

    return (
        <div className="pt-28 lg:pt-32" data-testid="compare-page">
            {/* Hero */}
            <section className="relative overflow-hidden py-16 lg:py-20">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // compare · the operating layer vs the silos
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="compare-title"
                        >
                            Same job. Same teams.
                            <br />
                            <span className="gradient-text">
                                Fewer tabs.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        ApexBrain is a single operating layer for the whole
                        company. Jira, Monday and ClickUp are excellent inside
                        their silo. Here&apos;s the honest side-by-side.
                    </p>
                </div>
            </section>

            {/* Layout 1 — side-by-side cards */}
            <section className="relative pb-16" data-testid="compare-cards">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                        {COMPARE_TOOLS.map((t, i) => (
                            <motion.div
                                key={t.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.07,
                                }}
                                whileHover={{ y: -4 }}
                                className={`relative rounded-2xl overflow-hidden p-7 backdrop-blur-sm transition-colors ${
                                    t.crown
                                        ? "border border-electric/40 bg-gradient-to-br " +
                                          t.gradient
                                        : "border border-white/[0.08] bg-obsidian-200/70 hover:border-white/15"
                                }`}
                                data-testid={`compare-card-${t.slug}`}
                            >
                                <div
                                    className={`absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gradient-to-br ${t.ring} blur-3xl pointer-events-none`}
                                />
                                {t.crown && (
                                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-electric/15 border border-electric/30 px-2 py-0.5 font-code text-[10px] text-electric uppercase tracking-[0.18em]">
                                        <Crown className="w-2.5 h-2.5" /> our pick
                                    </div>
                                )}
                                <div className="relative flex items-center gap-3">
                                    {t.id === "apex" ? (
                                        <img
                                            src="/favicon.png"
                                            alt="ApexBrain"
                                            className="w-9 h-9 rounded-lg shadow-[0_0_18px_rgba(94,234,212,0.35)]"
                                        />
                                    ) : (
                                        <span
                                            className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${t.accentBg}`}
                                        >
                                            <img
                                                src={
                                                    t.logo ??
                                                    `https://cdn.simpleicons.org/${t.slug}/ffffff`
                                                }
                                                alt={t.name}
                                                className="w-4 h-4 opacity-90"
                                            />
                                        </span>
                                    )}
                                    <div>
                                        <div className="font-outfit text-lg font-semibold text-white">
                                            {t.name}
                                        </div>
                                        <div
                                            className={`text-[10px] font-code uppercase tracking-[0.2em] ${t.accent}`}
                                        >
                                            {t.tag}
                                        </div>
                                    </div>
                                </div>

                                <div className="relative mt-6 grid grid-cols-2 gap-3 text-xs">
                                    <div className="rounded-lg bg-white/[0.04] border border-white/[0.06] p-2.5">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                            scope
                                        </div>
                                        <div className="font-semibold text-white mt-0.5">
                                            {t.scope}
                                        </div>
                                    </div>
                                    <div className="rounded-lg bg-white/[0.04] border border-white/[0.06] p-2.5">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                            price
                                        </div>
                                        <div className="font-semibold text-white mt-0.5">
                                            {t.price}
                                        </div>
                                        <div className="text-[10px] text-zinc-500 font-code mt-0.5">
                                            {t.seatMath}
                                        </div>
                                    </div>
                                </div>

                                <ul className="relative mt-6 space-y-2 pt-5 border-t border-white/[0.06]">
                                    {t.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-2 text-[13px] text-zinc-300 leading-snug"
                                        >
                                            {t.crown ? (
                                                <Sparkles
                                                    className={`shrink-0 w-3.5 h-3.5 mt-0.5 ${t.accent}`}
                                                />
                                            ) : (
                                                <Minus
                                                    className={`shrink-0 w-3.5 h-3.5 mt-0.5 ${t.accent}`}
                                                />
                                            )}
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <ModuleFeaturesSection
                eyebrow="// apexbrain modules · every plan"
                title="Nine modules."
                titleAccent="Every feature included."
                description="Dashboard, Sales, HR, Projects, Finance, Documents, Communications, Analytics and AI & Automation — full access on every plan. Competitors charge per module; ApexBrain ships the whole stack."
                showDepartment={false}
                pricingLink="/pricing"
                testId="compare-apex-modules"
            />

            {/* Layout 2 — Feature matrix */}
            <section
                className="relative py-16 lg:py-24"
                data-testid="compare-matrix"
            >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                        <div className="lg:col-span-7">
                            <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-3">
                                // feature matrix · the honest grid
                            </p>
                            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
                                Module-by-module
                                <br />
                                <span className="text-zinc-500">
                                    without the marketing.
                                </span>
                            </h2>
                        </div>
                        <div className="lg:col-span-5 flex flex-wrap items-center gap-3 text-[11px] font-code text-zinc-400">
                            <LegendDot v="yes" label="included" />
                            <LegendDot v="partial" label="partial" />
                            <LegendDot v="addon" label="add-on" />
                            <LegendDot v="no" label="not available" />
                        </div>
                    </div>

                    <div className="rounded-2xl border border-white/[0.08] bg-obsidian-200/60 backdrop-blur-sm overflow-hidden">
                        <div className="grid grid-cols-12 px-6 py-4 border-b border-white/[0.08] text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 bg-white/[0.02]">
                            <div className="col-span-5">// capability</div>
                            <div className="col-span-2 text-center text-electric">
                                ApexBrain
                            </div>
                            <div className="col-span-1 text-center text-blue-400">
                                Jira
                            </div>
                            <div className="col-span-2 text-center text-rose-300">
                                Monday
                            </div>
                            <div className="col-span-2 text-center text-violet-300">
                                ClickUp
                            </div>
                        </div>

                        {COMPARE_CATEGORIES.map((cat, i) => (
                            <div
                                key={cat.name}
                                className={
                                    i === 0
                                        ? ""
                                        : "border-t border-white/[0.06]"
                                }
                            >
                                <div className="px-6 py-4 bg-white/[0.02] flex items-center gap-3">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/[0.04] border border-white/[0.08]">
                                        <cat.icon className="w-3.5 h-3.5 text-electric" />
                                    </span>
                                    <span className="font-outfit text-sm font-semibold text-white">
                                        {cat.name}
                                    </span>
                                    <span className="text-[10px] font-code text-zinc-600 uppercase tracking-[0.14em]">
                                        {cat.rows.length} features
                                    </span>
                                </div>
                                {cat.rows.map((row, j) => (
                                    <motion.div
                                        key={row.label}
                                        initial={{ opacity: 0, x: -8 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.3, delay: j * 0.02 }}
                                        className="grid grid-cols-12 px-6 py-3.5 items-center border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                                        data-testid={`matrix-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        <div className="col-span-5 text-sm text-zinc-300">
                                            {row.label}
                                        </div>
                                        <div className="col-span-2 flex justify-center">
                                            <CellMark
                                                v={row.apex}
                                                accent="bg-electric/15 border-electric/40 text-electric"
                                            />
                                        </div>
                                        <div className="col-span-1 flex justify-center">
                                            <CellMark v={row.jira} />
                                        </div>
                                        <div className="col-span-2 flex justify-center">
                                            <CellMark v={row.monday} />
                                        </div>
                                        <div className="col-span-2 flex justify-center">
                                            <CellMark v={row.clickup} />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Layout 3 — deep dive switcher */}
            <section className="relative py-16" data-testid="compare-deepdive">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                        <div>
                            <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                                // deep dive · one-on-one
                            </p>
                            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-[1.05]">
                                ApexBrain head-to-head.
                            </h2>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {["jira", "monday", "clickup"].map((id) => {
                                const tool = COMPARE_TOOLS.find((t) => t.id === id);
                                const isActive = deepDive === id;
                                return (
                                    <button
                                        key={id}
                                        onClick={() => setDeepDive(id)}
                                        data-testid={`deepdive-tab-${id}`}
                                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
                                            isActive
                                                ? `${tool.accentBg} text-white`
                                                : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
                                        }`}
                                    >
                                        <span
                                            className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400 animate-pulse-glow" : "bg-zinc-600"}`}
                                        />
                                        vs {tool.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.article
                            key={deepDive}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } }}
                            exit={{ opacity: 0, y: -6, transition: { duration: 0.18, ease: [0.4, 0, 1, 1] } }}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-6"
                            data-testid={`deepdive-content-${deepDive}`}
                        >
                            <div className="lg:col-span-5 rounded-2xl border border-white/[0.08] bg-obsidian-200/70 backdrop-blur-sm p-7 sm:p-8">
                                <p className="font-code text-[10px] uppercase tracking-[0.2em] text-electric mb-3">
                                    // the headline
                                </p>
                                <h3 className="font-outfit text-2xl sm:text-3xl font-semibold text-white leading-snug">
                                    {active.title}
                                </h3>
                                <p className="mt-3 text-cyan-300 text-sm font-medium">
                                    {active.sub}
                                </p>
                                <p className="mt-5 text-sm sm:text-base text-zinc-400 leading-relaxed font-light">
                                    {active.story}
                                </p>
                            </div>

                            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="rounded-2xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-6 relative overflow-hidden">
                                    <div className="absolute -top-20 -right-20 w-48 h-48 bg-electric/15 blur-3xl rounded-full pointer-events-none" />
                                    <p className="relative font-code text-[10px] uppercase tracking-[0.2em] text-electric mb-3">
                                        // where apexbrain wins
                                    </p>
                                    <ul className="relative space-y-3">
                                        {active.wins.map((w) => (
                                            <li
                                                key={w}
                                                className="flex items-start gap-2 text-sm text-zinc-200"
                                            >
                                                <Sparkles className="shrink-0 w-3.5 h-3.5 mt-0.5 text-electric" />
                                                <span>{w}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="rounded-2xl border border-white/[0.08] bg-obsidian-200/70 p-6 relative overflow-hidden">
                                    <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-glow mb-3">
                                        // what you don&apos;t lose
                                    </p>
                                    <ul className="space-y-3">
                                        {active.keepFromJira.map((k) => (
                                            <li
                                                key={k}
                                                className="flex items-start gap-2 text-sm text-zinc-300"
                                            >
                                                <Check className="shrink-0 w-3.5 h-3.5 mt-0.5 text-amber-glow" />
                                                <span>{k}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.article>
                    </AnimatePresence>
                </div>
            </section>

            {/* Layout 4 — Pricing snapshot */}
            {/* <section
                className="relative py-16 lg:py-20"
                data-testid="compare-pricing"
            >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="mb-10">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-emerald-300 font-semibold mb-3">
                            // pricing snapshot · 20 seats, 1 year
                        </p>
                        <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight text-white leading-[1.05]">
                            What the bill actually looks like.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            {
                                name: "ApexBrain Growth",
                                slug: "apex",
                                price: "$5,760",
                                line: "all 6 pillars + AI included",
                                note: "Free under 8 seats. $24/seat/mo from there.",
                                hero: true,
                            },
                            {
                                name: "Jira Premium + add-ons",
                                slug: "jira",
                                price: "$3,432",
                                line: "engineering only — others bought separately",
                                note: "Add Service Desk, Confluence and AI: +$4,800/yr",
                            },
                            {
                                name: "Monday + CRM + Dev",
                                slug: "monday",
                                price: "$5,760",
                                line: "three separate products, three boards",
                                note: "AI on Pro Suite tier ~ +$2,400/yr",
                            },
                            {
                                name: "ClickUp Business + AI",
                                slug: "clickup",
                                price: "$4,560",
                                line: "task + doc surface · AI is paid add-on",
                                note: "AI quotas re-billed by usage",
                            },
                        ].map((p, i) => {
                            const tool = TOOLS.find((t) => t.id === p.slug);
                            return (
                                <motion.div
                                    key={p.name}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.45, delay: i * 0.06 }}
                                    whileHover={{ y: -3 }}
                                    className={`relative rounded-2xl p-6 overflow-hidden ${
                                        p.hero
                                            ? "border border-electric/40 bg-gradient-to-br from-electric/15 to-emerald-400/10"
                                            : "border border-white/[0.08] bg-obsidian-200/70"
                                    }`}
                                    data-testid={`pricing-snap-${p.slug}`}
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <span
                                            className={`w-1.5 h-1.5 rounded-full ${p.hero ? "bg-emerald-400 animate-pulse-glow" : "bg-zinc-600"}`}
                                        />
                                        <span
                                            className={`text-[10px] font-code uppercase tracking-[0.18em] ${tool.accent}`}
                                        >
                                            {tool.name}
                                        </span>
                                    </div>
                                    <div className="font-outfit text-3xl font-semibold text-white tabular-nums">
                                        {p.price}
                                    </div>
                                    <div className="text-[11px] text-zinc-500 font-code mt-1">
                                        /yr · 20 seats
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-white/[0.06]">
                                        <div className="text-sm font-semibold text-white">
                                            {p.name}
                                        </div>
                                        <div className="text-xs text-zinc-400 mt-1">
                                            {p.line}
                                        </div>
                                        <div className="text-[11px] text-zinc-600 mt-3 font-code">
                                            {p.note}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section> */}

            <EveryPlanIncludesSection
                pricingLink="/pricing"
                testId="compare-every-plan"
            />

            {/* Layout 5 — Migration callout */}
            <section className="relative py-16" data-testid="compare-migration">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-obsidian-100 via-obsidian-200 to-obsidian-300 p-10 sm:p-14 overflow-hidden">
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[70%] h-40 bg-electric/25 blur-[120px] rounded-full pointer-events-none" />
                        <div className="absolute inset-0 grid-bg opacity-[0.12] pointer-events-none" />
                        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-7">
                                <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-glow font-semibold">
                                    // migration · in a day
                                </p>
                                <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mt-4 leading-[1.05]">
                                    Coming from Jira, Monday or ClickUp?
                                    <br />
                                    <span className="gradient-text">
                                        We move it for you.
                                    </span>
                                </h2>
                                <p className="mt-5 max-w-2xl text-zinc-400 font-light leading-relaxed">
                                    One-click importers for issues, boards,
                                    docs and people. A dedicated migration
                                    engineer for teams over 50. No data is left
                                    behind.
                                </p>
                                <div className="mt-7 flex flex-wrap items-center gap-3">
                                    <a
                                        href="/startups"
                                        className="group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-6 py-3 rounded-full hover:brightness-110 transition"
                                        data-testid="compare-cta-primary"
                                    >
                                        Start a free migration
                                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                                    </a>
                                    {/* <a
                                        href="/docs"
                                        className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-6 py-3 rounded-full hover:bg-white/10 transition"
                                        data-testid="compare-cta-secondary"
                                    >
                                        Read the migration docs
                                        <ArrowUpRight className="w-4 h-4 text-electric" />
                                    </a> */}
                                </div>
                            </div>

                            <div className="lg:col-span-5">
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { v: "1 day", l: "typical move" },
                                        { v: "100%", l: "data preserved" },
                                        { v: "0", l: "downtime" },
                                        { v: "$0", l: "to migrate" },
                                        { v: "24/7", l: "white-glove" },
                                        { v: "+62", l: "supported imports" },
                                    ].map((m) => (
                                        <div
                                            key={m.l}
                                            className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-3 text-center"
                                        >
                                            <div className="font-outfit text-lg font-semibold text-white tabular-nums">
                                                {m.v}
                                            </div>
                                            <div className="text-[10px] text-zinc-500 font-code mt-0.5 uppercase tracking-[0.18em]">
                                                {m.l}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Closing line */}
            <section className="relative pb-28 pt-10">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
                    <p className="font-code text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                        // the bottom line
                    </p>
                    <p className="mt-3 font-outfit text-xl sm:text-2xl text-white leading-snug">
                        Jira, Monday and ClickUp are good at one thing.{" "}
                        <span className="gradient-text">
                            ApexBrain runs the whole company.
                        </span>
                    </p>
                </div>
            </section>
        </div>
    );
}

const LegendDot = ({ v, label }) => (
    <span className="inline-flex items-center gap-1.5">
        <CellMark v={v} />
        <span>{label}</span>
    </span>
);
